/**
 * useMultilang.ts
 * ---------------
 * React hook that provides multilang filtering tied to the active UI locale.
 *
 * Returns:
 *   ml(text)   — parse multilang tags for the current locale + sanitize
 *   lang       — active locale code (e.g. 'en', 'ar')
 *   isRTL      — true when the locale is right-to-left
 */

import { useCallback } from 'react';
import { useT } from '@/i18n';
import { parseMultilang } from '@/utils/multilang';

export interface UseMultilangResult {
  /** Filter a backend string for the current language. */
  ml: (text: string | null | undefined) => string;
  /** Current locale code */
  lang: string;
  /** Is the current locale right-to-left? */
  isRTL: boolean;
}

export function useMultilang(): UseMultilangResult {
  const { locale, isRTL } = useT();

  const ml = useCallback(
    (text: string | null | undefined): string => {
      if (!text) return '';
      // Parse multilang blocks for the active locale.
      // React automatically escapes text nodes — no additional sanitization needed here.
      // Use sanitizeHtml() explicitly only when rendering via dangerouslySetInnerHTML.
      return parseMultilang(text, locale);
    },
    [locale],
  );

  return { ml, lang: locale, isRTL };
}
